<template>
    <div class="laragle-input mt-8"
        @click="handleFocus"
    >
        <div 
            class="flex items-center mb-2 border-b"
            :class="{
                'border-blue-500': localFocus && !disable, 
                'border-gray-400': (!localFocus && !error) || disable,
                'border-red-500': error
            }"
        >
            <div v-if="prefix">{{ prefix }}</div>
            <input :type="type == 'password' && !passwordMasked ? 'text' : type"
                class="form-control"
                :class="{ error, 'text-gray-500': disable, 'text-gray-700': !disable }"
                :value="value"
                @input="$emit('input', $event.target.value)"
                @keyup="handleKeyup"
                v-focus="focus"
                v-lowercase="lowercase"
                v-titlecase="titlecase"
                v-alpha-dash="alphaDash"
                v-alpha-numeric="alphaNumeric"
                v-limit="limit"
                v-integer="integer"
                v-float="float"
                @focus="handleFocus"
                @blur="handleBlur"
                :disabled="disable"
                ref="input"
            >
            <div v-if="appendText" class="text-gray-500">{{ appendText }}</div>
            <app-spinner class="ml-2 text-gray-500" v-if="loading" />
            <check-icon class="ml-2 text-green-500" v-if="!loading && checked"/>
            <visible-icon 
                class="ml-2 cursor-pointer" 
                v-if="type == 'password' && !passwordMasked"
                @click.native="passwordMasked = true" 
            />
            <invisible-icon 
                class="ml-2 cursor-pointer" 
                v-if="type == 'password' && passwordMasked"
                @click.native="passwordMasked = false"
            />
            <component 
                v-if="action.icon && !loading" 
                :is="action.icon"
                class="ml-2 cursor-pointer text-gray-500 hover:text-gray-800"
                :title="action.label"
                @click.native="action.handler(value)"
            />            
        </div>
        <label 
            :class="{active, error}"
            @click="$refs.input.focus()"
        ><span class="text-red-500" v-if="required">*</span> {{ placeholder }}</label>
        <div class="error"
            v-if="error"
            v-html="error"></div>
        <div class="help-text"
            v-if="helpText"
            v-html="helpText"></div>
    </div>
</template>
<script>
    import isEmpty from 'lodash/isEmpty'
    export default {
        components: {
            CheckIcon: () => import('@/icons/Check.vue'),
            VisibleIcon: () => import('@/icons/VisibilityOutlined.vue'),
            InvisibleIcon: () => import('@/icons/VisibilityOffOutlined.vue'),
        },
        props: {
            value: '',
            type: {
                type: String,
                default: 'text'
            },
            placeholder: {
                type: String,
                required: true
            },
            focus: {
                type: Boolean,
                default: false
            },
            lowercase: {
                type: Boolean,
                default: false
            },
            alphaDash: {
                type: Boolean,
                default: false
            },
            alphaNumeric: {
                type: Boolean,
                default: false,
            },
            titlecase: {
                type: Boolean,
                default: false
            },
            limit: {
                type: Number,
                default: 0
            },
            integer: {
                type: Boolean,
                default: false
            },
            float: {
                type: Boolean,
                default: false,
            },
            error: {
                type: String,
                default: ''
            },
            helpText: {
                type: String,
                default: null
            },
            prefix: {
                type: String,
                default: null
            },
            loading: {
                type: Boolean,
                default: false
            },
            required: {
                type: Boolean,
                default: false
            },
            disable: {
                type: Boolean,
                default: false
            },
            checked: {
                type: Boolean,
                default: false,
            },
            action: {
                type: Object,
                default() {
                    return {}
                }
            },
            appendText: {
                type: String,
                default: null,
            }
        },
        data() {
            return {
                active: false,
                localFocus: false,
                passwordMasked: true,
            }
        },
        watch: {
            value(value) {
                this.active = (value !== null && value !== '') || this.prefix || this.localFocus;
            }
        },
        mounted() {
            this.active = (this.value !== null && this.value !== '') || this.focus || this.prefix
        },
        methods: {
            handleKeyup() {
                this.$emit('updated', this.value)
            },
            handleFocus() {
                this.$emit('focus')
                this.active = true
                this.localFocus = true
                this.$refs.input.focus()
            },
            handleBlur() {
                this.$emit('blur')
                this.localFocus = false

                if ((this.value === null || this.value === '') && !this.prefix) {
                    this.active = false
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .laragle-input {
        position: relative;
        text-align: left;
        label {
            @apply text-gray-600;
            position: absolute;
            top: 0;
            left: 0;
            cursor: text;
            -webkit-transition: color .2s ease-out, -webkit-transform .2s ease-out;
            transition: color .2s ease-out, -webkit-transform .2s ease-out;
            transition: transform .2s ease-out, color .2s ease-out;
            transition: transform .2s ease-out, color .2s ease-out, -webkit-transform .2s ease-out;
            -webkit-transform-origin: 0% 100%;
            transform-origin: 0% 100%;
            text-align: initial;
            -webkit-transform: translateY(14px);
            transform: translateY(14px);
            &.active {
                -webkit-transform: translateY(-14px) scale(.9);
                transform: translateY(-14px) scale(.9);
                -webkit-transform-origin: 0 0;
                transform-origin: 0 0;
                &.error {
                    @apply text-red-500;
                }
            }
        }
        .form-control {
            @apply text-sm;
            line-height: 1.5;
            display: block;
            background-color: transparent;
            border-radius: 0;
            outline: none;
            min-height: 3rem;
            width: 100%;
            padding: 0;
            -webkit-box-shadow: none;
            box-shadow: none;
            -webkit-box-sizing: content-box;
            box-sizing: content-box;
            -webkit-transition: border .3s, -webkit-box-shadow .3s;
            transition: border .3s, -webkit-box-shadow .3s;
            transition: box-shadow .3s, border .3s;
            transition: box-shadow .3s, border .3s, -webkit-box-shadow .3s;            
        }
        .error {
            @apply text-red-500;
            font-size: .875rem;            
        }
        .help-text {
            @apply text-gray-500;
            font-size: .875rem;
            margin-top: 0.25rem;
        }
    }
</style>